import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  BarChart3, 
  Users, 
  Presentation, 
  BookOpen, 
  Scale, 
  Mail, 
  MapPin,
  Menu,
  X,
  ChevronRight,
  Check,
  ArrowRight,
  Heart,
  CreditCard,
  RefreshCw,
  DollarSign,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo Component - Using official Centerra Project logo image
const CenterraLogo = ({ className = "", size = "default", variant = "dark" }) => {
  const height = size === "large" ? "h-20" : "h-16";
  
  return (
    <div className={`flex items-center ${className}`}>
      <img 
        src="/centerra-logo.jpg"
        alt="The Centerra Project"
        className={`${height} w-auto object-contain`}
      />
    </div>
  );
};

// Navbar Component - Always white background
const Navbar = ({ isDonate = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { href: "/#mission", label: "Mission" },
    { href: "/#approach", label: "Our Approach" },
    { href: "/#invest", label: "Get Involved" },
    { href: "/#contact", label: "Contact" }
  ];

  return (
    <nav 
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-24">
          <a href="/" data-testid="logo-link">
            <CenterraLogo />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
                className="text-sm font-medium transition-colors hover:opacity-80 text-slate-700"
              >
                {link.label}
              </a>
            ))}
            <a href="https://app.oath.vote/donate?p=centerra-c4" target="_blank" rel="noopener noreferrer">
              <Button 
                data-testid="nav-donate-button"
                className="bg-centerra-orange hover:bg-centerra-orange/90 text-black rounded-full px-6 py-2 font-semibold btn-centerra"
              >
                <Heart className="w-4 h-4 mr-2" />
                Donate
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-slate-700"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div data-testid="mobile-menu" className="md:hidden bg-white rounded-2xl shadow-xl p-6 mt-2 animate-fade-up">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 text-slate-700 font-medium border-b border-slate-100 last:border-0"
              >
                {link.label}
              </a>
            ))}
            <a href="https://app.oath.vote/donate?p=centerra-c4" target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>
              <Button className="w-full mt-4 bg-centerra-orange hover:bg-centerra-orange/90 text-black rounded-full">
                <Heart className="w-4 h-4 mr-2" />
                Donate
              </Button>
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section - add padding top for fixed navbar
const HeroSection = () => {
  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-24"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1605751216159-fc4057eba116?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920')`,
        backgroundSize: "cover",
        backgroundPosition: "center"
      }}
    >
      <div className="absolute inset-0 hero-overlay pointer-events-none" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32">
        <div className="max-w-3xl">
          <p className="text-centerra-orange text-sm font-semibold tracking-widest uppercase mb-4 animate-fade-up">
            Nonpartisan • Data-Driven • Solutions-Focused
          </p>
          <h1 
            data-testid="hero-headline"
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fade-up delay-100"
          >
            Pragmatic Leadership for New Mexico's Future
          </h1>
          <p 
            data-testid="hero-subheadline"
            className="text-lg md:text-xl text-white/80 leading-relaxed mb-10 animate-fade-up delay-200"
          >
            The Centerra Project connects business leaders, civic organizers, and policy advocates 
            to drive measurable change through collaboration, data, and shared community values.
          </p>
          <div className="flex flex-wrap gap-4 animate-fade-up delay-300">
            <a href="https://app.oath.vote/donate?p=centerra-c4" target="_blank" rel="noopener noreferrer">
              <Button 
                data-testid="hero-cta-primary"
                className="bg-centerra-orange hover:bg-centerra-orange/90 text-white rounded-full px-8 py-6 text-lg font-semibold btn-centerra shadow-lg"
              >
                <Heart className="mr-2 h-5 w-5" />
                Support Our Mission
              </Button>
            </a>
            <a href="#mission">
              <Button 
                data-testid="hero-cta-secondary"
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/30 rounded-full px-8 py-6 text-lg font-medium backdrop-blur-sm"
              >
                Learn More
              </Button>
            </a>
          </div>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
          <div className="w-1 h-3 bg-white/70 rounded-full" />
        </div>
      </div>
    </section>
  );
};

// Mission Section
const MissionSection = () => {
  return (
    <section id="mission" data-testid="mission-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <span className="text-centerra-orange text-sm font-semibold tracking-widest uppercase">
              Why Centerra Exists
            </span>
            <h2 
              data-testid="mission-headline"
              className="font-heading text-3xl md:text-4xl font-bold text-black mt-3 mb-6"
            >
              Restoring Balance to New Mexico Politics
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-6">
              New Mexico's progress has often been constrained by an imbalance of influence—where 
              ideological agendas often outweigh practical problem-solving. Essential reforms in 
              education, healthcare, public safety, and economic development have struggled to gain traction.
            </p>
            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              <strong className="text-centerra-purple">Centerra exists to restore balance</strong>—empowering 
              business and civic leaders to advance policies grounded in data, collaboration, and 
              shared community values.
            </p>
            <div className="flex items-center gap-4">
              <div className="flex -space-x-3">
                <div className="w-10 h-10 rounded-full bg-centerra-purple flex items-center justify-center text-white text-sm font-bold">B</div>
                <div className="w-10 h-10 rounded-full bg-centerra-purple flex items-center justify-center text-white text-sm font-bold">C</div>
                <div className="w-10 h-10 rounded-full bg-centerra-orange flex items-center justify-center text-white text-sm font-bold">P</div>
              </div>
              <span className="text-slate-500 text-sm">Business • Civic • Policy Leaders United</span>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-centerra-purple/10 rounded-3xl -z-10" />
            <img 
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
              alt="Leadership team in discussion"
              className="rounded-2xl shadow-xl w-full h-auto"
              data-testid="mission-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Five Pillars Section
const PillarsSection = () => {
  const pillars = [
    {
      icon: BarChart3,
      title: "District Analysis",
      description: "Data-driven research on voter behavior, economic conditions, and community priorities."
    },
    {
      icon: Users,
      title: "Leadership Development",
      description: "Mentoring pragmatic, business-minded leaders with skills to navigate modern governance."
    },
    {
      icon: Presentation,
      title: "Campaign Management",
      description: "Practical training and technical support for issue advocates and civic organizations."
    },
    {
      icon: BookOpen,
      title: "Voter Education",
      description: "Civic awareness through educational programs and public dialogues."
    }
  ];

  return (
    <section id="approach" data-testid="pillars-section" className="py-20 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-16">
          <span className="text-centerra-orange text-sm font-semibold tracking-widest uppercase">
            Our Methodology
          </span>
          <h2 
            data-testid="pillars-headline"
            className="font-heading text-3xl md:text-4xl font-bold text-black mt-3"
          >
            We Invest in People, Talent, and Infrastructure
          </h2>
        </div>
        
        {/* Four uniform pillars in a row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {pillars.map((pillar, index) => (
            <Card 
              key={pillar.title}
              data-testid={`pillar-card-${index}`}
              className="pillar-card bg-white border-0 shadow-sm hover:shadow-lg rounded-2xl overflow-hidden h-full"
            >
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-centerra-purple/10">
                  <pillar.icon className="w-6 h-6 text-centerra-purple" />
                </div>
                <h3 className="font-heading text-lg font-bold mb-2 text-black">
                  {pillar.title}
                </h3>
                <p className="text-sm leading-relaxed text-slate-600">
                  {pillar.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Policy Reform spanning full width */}
        <Card className="bg-centerra-purple border-0 shadow-lg rounded-2xl overflow-hidden">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-white/10 flex-shrink-0">
                <Scale className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-xl font-bold mb-2 text-white">
                  Policy Reform & Advocacy
                </h3>
                <p className="text-white/80 leading-relaxed mb-3">
                  We promote data-informed, consensus-driven policy solutions that align economic growth with social welfare, advancing reforms that strengthen New Mexico's long-term prosperity.
                </p>
                <p className="text-white font-semibold">
                  Focus Areas: Education, Healthcare, Public Safety, Economic Development
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

// Why Now Section
const WhyNowSection = () => {
  return (
    <section data-testid="why-now-section" className="py-20 md:py-28 bg-black">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
        <span className="text-centerra-orange text-sm font-semibold tracking-widest uppercase">
          Why Now?
        </span>
        <h2 
          data-testid="why-now-headline"
          className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-4 mb-6 max-w-4xl mx-auto"
        >
          New Mexico is at a Turning Point
        </h2>
        <p className="text-xl text-white/80 max-w-3xl mx-auto leading-relaxed mb-10">
          Voters, businesses, and communities are ready for pragmatic, solutions-driven leadership 
          that rises above division. This is the moment to unite data, education, and advocacy 
          into a coordinated effort that strengthens our state's civic and economic future.
        </p>
        <a href="https://app.oath.vote/donate?p=centerra-c4" target="_blank" rel="noopener noreferrer">
          <Button 
            data-testid="why-now-cta"
            className="bg-centerra-orange text-black hover:bg-centerra-orange/90 rounded-full px-10 py-6 text-lg font-bold btn-centerra shadow-lg"
          >
            <Heart className="mr-2 h-5 w-5" />
            Support the Movement
          </Button>
        </a>
      </div>
    </section>
  );
};

// Vision Section
const VisionSection = () => {
  return (
    <section data-testid="vision-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="relative order-2 md:order-1">
            <div className="absolute -inset-4 bg-centerra-orange/10 rounded-3xl -z-10 rotate-2" />
            <img 
              src="/kite-sunset.jpg"
              alt="Children flying a kite at sunset"
              className="rounded-2xl shadow-xl w-full h-auto object-cover"
              data-testid="vision-image"
            />
          </div>
          <div className="order-1 md:order-2">
            <span className="text-centerra-orange text-sm font-semibold tracking-widest uppercase">
              What Success Looks Like
            </span>
            <h2 
              data-testid="vision-headline"
              className="font-heading text-3xl md:text-4xl font-bold text-black mt-3 mb-6"
            >
              Our Vision for New Mexico
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              Our vision is to transform the relationship between business, community, and policy—creating 
              a shared foundation where opportunity, innovation, and social responsibility advance together 
              for the common good of New Mexico.
            </p>
            <div className="space-y-4">
              {[
                "A thriving, diversified economy",
                "Engaged and informed voters",
                "Accountable, pragmatic leadership",
                "Data-driven policy decisions"
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-centerra-purple/10 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-centerra-purple" />
                  </div>
                  <span className="text-slate-700 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Get Involved Section (Donate + Take Action)
const InvestmentSection = () => {
  return (
    <section id="invest" data-testid="investment-section" className="py-20 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-16">
          <span className="text-centerra-orange text-sm font-semibold tracking-widest uppercase">
            Get Involved
          </span>
          <h2 
            data-testid="investment-headline"
            className="font-heading text-3xl md:text-4xl font-bold text-black mt-3 mb-4"
          >
            Partner With Us
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Your partnership fuels pragmatic leadership and informed advocacy—creating the civic 
            foundation for long-term prosperity and progress in New Mexico.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Donate Box */}
          <Card className="bg-white rounded-2xl border-2 border-centerra-orange shadow-sm hover:shadow-xl">
            <CardHeader className="text-center pb-2 pt-8">
              <div className="w-16 h-16 rounded-full bg-centerra-orange/10 flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-centerra-orange" />
              </div>
              <CardTitle className="font-heading text-2xl font-bold text-black">
                Donate
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 pb-8 text-center">
              <p className="text-slate-600 mb-6 leading-relaxed">
                Your financial contribution directly powers our mission to build pragmatic leadership 
                in New Mexico. Every donation—big or small—helps us conduct research, train leaders, 
                and advocate for common-sense policies that benefit all New Mexicans.
              </p>
              <ul className="text-left space-y-2 mb-8 max-w-xs mx-auto">
                <li className="flex items-center gap-2 text-slate-600 text-sm">
                  <Check className="w-4 h-4 text-centerra-purple" />
                  Fund leadership development programs
                </li>
                <li className="flex items-center gap-2 text-slate-600 text-sm">
                  <Check className="w-4 h-4 text-centerra-purple" />
                  Support data-driven policy research
                </li>
                <li className="flex items-center gap-2 text-slate-600 text-sm">
                  <Check className="w-4 h-4 text-centerra-purple" />
                  Enable voter education initiatives
                </li>
              </ul>
              <a href="https://app.oath.vote/donate?p=centerra-c4" target="_blank" rel="noopener noreferrer">
                <Button 
                  data-testid="donate-cta"
                  className="w-full rounded-full py-5 font-semibold btn-centerra bg-centerra-orange hover:bg-centerra-orange/90 text-black"
                >
                  <Heart className="w-5 h-5 mr-2" />
                  Donate
                </Button>
              </a>
            </CardContent>
          </Card>

          {/* Take Action Box */}
          <Card className="bg-white rounded-2xl border-2 border-centerra-purple shadow-sm hover:shadow-xl">
            <CardHeader className="text-center pb-2 pt-8">
              <div className="w-16 h-16 rounded-full bg-centerra-purple/10 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-centerra-purple" />
              </div>
              <CardTitle className="font-heading text-2xl font-bold text-black">
                Take Action
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 pb-8 text-center">
              <p className="text-slate-600 mb-6 leading-relaxed">
                There are many ways to support Centerra beyond donations. Join our community of 
                engaged citizens working to create positive change. Whether you have time, skills, 
                or connections to share, we'd love to have you on board.
              </p>
              <ul className="text-left space-y-2 mb-8 max-w-xs mx-auto">
                <li className="flex items-center gap-2 text-slate-600 text-sm">
                  <Check className="w-4 h-4 text-centerra-purple" />
                  Volunteer at events and campaigns
                </li>
                <li className="flex items-center gap-2 text-slate-600 text-sm">
                  <Check className="w-4 h-4 text-centerra-purple" />
                  Stay informed and spread the word
                </li>
                <li className="flex items-center gap-2 text-slate-600 text-sm">
                  <Check className="w-4 h-4 text-centerra-purple" />
                  Connect us with community leaders
                </li>
              </ul>
              <a href="/volunteer">
                <Button 
                  data-testid="action-cta"
                  className="w-full rounded-full py-5 font-semibold btn-centerra bg-centerra-purple hover:bg-centerra-purple/90 text-white"
                >
                  Get Started
                </Button>
              </a>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    message: ""
  });
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setFormData({ name: "", email: "", organization: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterSubmitting(true);
    
    try {
      await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      toast.success("Successfully subscribed to our newsletter!");
      setNewsletterEmail("");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info("This email is already subscribed.");
      } else {
        toast.error("Failed to subscribe. Please try again.");
      }
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Form */}
          <div>
            <span className="text-centerra-orange text-sm font-semibold tracking-widest uppercase">
              Get in Touch
            </span>
            <h2 
              data-testid="contact-headline"
              className="font-heading text-3xl md:text-4xl font-bold text-black mt-3 mb-6"
            >
              Let's Start a Conversation
            </h2>
            <p className="text-slate-600 text-lg mb-8">
              Ready to make a difference? Reach out to learn more about partnership opportunities 
              or how you can get involved.
            </p>

            <form onSubmit={handleContactSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <Input
                  data-testid="contact-name-input"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="h-12 rounded-lg border-slate-200 focus:border-centerra-purple focus:ring-centerra-purple"
                />
                <Input
                  data-testid="contact-email-input"
                  type="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-12 rounded-lg border-slate-200 focus:border-centerra-purple focus:ring-centerra-purple"
                />
              </div>
              <Input
                data-testid="contact-org-input"
                placeholder="Organization (Optional)"
                value={formData.organization}
                onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                className="h-12 rounded-lg border-slate-200 focus:border-centerra-purple focus:ring-centerra-purple"
              />
              <Textarea
                data-testid="contact-message-input"
                placeholder="Your Message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows={5}
                className="rounded-lg border-slate-200 focus:border-centerra-purple focus:ring-centerra-purple resize-none"
              />
              <Button
                data-testid="contact-submit-button"
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto bg-centerra-purple hover:bg-centerra-purple/90 text-white rounded-full px-10 py-5 font-semibold btn-centerra"
              >
                {submitting ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </div>

          {/* Newsletter & Info */}
          <div>
            <div className="bg-slate-50 rounded-2xl p-8 mb-8">
              <h3 className="font-heading text-xl font-bold text-black mb-3">
                Stay Informed
              </h3>
              <p className="text-slate-600 mb-5">
                Subscribe to our newsletter for updates on policy developments, events, and opportunities.
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-3">
                <Input
                  data-testid="newsletter-email-input"
                  type="email"
                  placeholder="Your email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  required
                  className="flex-1 h-12 rounded-full border-slate-200 focus:border-centerra-purple focus:ring-centerra-purple"
                />
                <Button
                  data-testid="newsletter-submit-button"
                  type="submit"
                  disabled={newsletterSubmitting}
                  className="bg-centerra-purple hover:bg-centerra-purple/90 text-white rounded-full px-6 font-semibold btn-centerra"
                >
                  {newsletterSubmitting ? "..." : "Subscribe"}
                </Button>
              </form>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-centerra-purple/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-centerra-purple" />
                </div>
                <div>
                  <h4 className="font-semibold text-centerra-purple">Email</h4>
                  <p className="text-slate-600">info@centerraproject.org</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-centerra-purple/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-centerra-purple" />
                </div>
                <div>
                  <h4 className="font-semibold text-centerra-purple">Location</h4>
                  <p className="text-slate-600">Albuquerque, New Mexico</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-black py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid md:grid-cols-3 gap-12">
          <div className="md:col-span-2">
            <CenterraLogo className="mb-6" size="large" variant="light" />
            <p className="text-white/70 leading-relaxed max-w-md">
              A unified, nonpartisan effort that brings together business and civic leaders 
              to strengthen the infrastructure for pragmatic leadership and policy solutions 
              that shape New Mexico's future.
            </p>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { label: "Mission", href: "/#mission" },
                { label: "Our Approach", href: "/#approach" },
                { label: "Get Involved", href: "/#invest" },
                { label: "Contact", href: "/#contact" },
                { label: "Donate", href: "https://app.oath.vote/donate?p=centerra-c4", external: true },
                { label: "Volunteer", href: "/volunteer" }
              ].map((link) => (
                <li key={link.label}>
                  <a 
                    href={link.href}
                    target={link.external ? "_blank" : undefined}
                    rel={link.external ? "noopener noreferrer" : undefined}
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3 text-white/50 text-sm">
            <span>© {new Date().getFullYear()} The Centerra Project. All rights reserved.</span>
            <span className="hidden md:inline">•</span>
            <a 
              href="https://www.amadormarketing.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
            >
              Powered by A/M/S
            </a>
          </div>
          <p className="text-white/50 text-sm">
            Nonpartisan • Data-Driven • New Mexico Focused
          </p>
        </div>
      </div>
    </footer>
  );
};

// Donation Page Component
const DonatePage = () => {
  const [searchParams] = useSearchParams();
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const presetAmounts = [
    { id: "preset_250", amount: 250, label: "$250" },
    { id: "preset_500", amount: 500, label: "$500" },
    { id: "preset_1000", amount: 1000, label: "$1,000" },
    { id: "preset_4000", amount: 4000, label: "$4,000" },
    { id: "preset_10000", amount: 10000, label: "$10,000" }
  ];

  // Check for success/cancel params on mount
  useEffect(() => {
    const success = searchParams.get("success");
    const sessionId = searchParams.get("session_id");
    const cancelled = searchParams.get("cancelled");

    if (success === "true" && sessionId) {
      pollPaymentStatus(sessionId);
    } else if (cancelled === "true") {
      toast.info("Donation cancelled. Feel free to try again when you're ready.");
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error("Payment status check timed out. Please check your email for confirmation.");
      return;
    }

    setCheckingStatus(true);

    try {
      const response = await axios.get(`${API}/donations/stripe/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setPaymentSuccess(true);
        setCheckingStatus(false);
        toast.success("Thank you for your generous donation!");
      } else if (response.data.status === "expired") {
        setCheckingStatus(false);
        toast.error("Payment session expired. Please try again.");
      } else {
        // Continue polling
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      setCheckingStatus(false);
      toast.error("Error checking payment status.");
    }
  };

  const handleDonate = async (provider) => {
    // Validate amount
    let amount = null;
    let presetId = null;

    if (selectedAmount) {
      presetId = selectedAmount;
    } else if (customAmount) {
      amount = parseFloat(customAmount);
      if (isNaN(amount) || amount < 5) {
        toast.error("Minimum donation is $5");
        return;
      }
      if (amount > 100000) {
        toast.error("Maximum donation is $100,000");
        return;
      }
    } else {
      toast.error("Please select or enter a donation amount");
      return;
    }

    setLoading(true);

    try {
      if (provider === "stripe") {
        const response = await axios.post(`${API}/donations/stripe/checkout`, {
          preset_id: presetId,
          custom_amount: amount,
          is_recurring: isRecurring,
          donor_name: donorName || null,
          donor_email: donorEmail || null,
          origin_url: window.location.origin
        });

        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else if (provider === "paypal") {
        toast.info("PayPal integration coming soon! Please use card payment for now.");
        setLoading(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process donation. Please try again.");
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-centerra-purple animate-spin mx-auto mb-4" />
          <p className="text-centerra-purple font-semibold">Verifying your donation...</p>
        </div>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar isDonate={true} />
        <div className="pt-32 pb-20 px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-centerra-purple/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-centerra-purple" />
            </div>
            <h1 className="font-heading text-4xl font-bold text-black mb-4">
              Thank You for Your Support!
            </h1>
            <p className="text-slate-600 text-lg mb-8">
              Your generous donation helps us advance pragmatic leadership and policy solutions 
              for New Mexico's future. A confirmation email will be sent shortly.
            </p>
            <div className="flex justify-center gap-4">
              <a href="/">
                <Button className="bg-centerra-purple hover:bg-centerra-purple/90 text-white rounded-full px-8 py-5">
                  Return Home
                </Button>
              </a>
              <a href="https://app.oath.vote/donate?p=centerra-c4" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-centerra-purple text-centerra-purple rounded-full px-8 py-5">
                  Donate Again
                </Button>
              </a>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar isDonate={true} />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <span className="text-centerra-orange text-sm font-semibold tracking-widest uppercase">
              Support Our Mission
            </span>
            <h1 
              data-testid="donate-headline"
              className="font-heading text-4xl md:text-5xl font-bold text-black mt-3 mb-4"
            >
              Make a Donation
            </h1>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Your contribution fuels pragmatic leadership and informed advocacy—creating the 
              civic foundation for long-term prosperity in New Mexico.
            </p>
            <p className="text-slate-500 text-sm italic max-w-2xl mx-auto mt-4">
              The Centerra Project is a 501(c)(4) organization. Contributions to The Centerra Project 
              are not deductible for federal income tax purposes as charitable contributions.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Donation Form */}
            <div className="md:col-span-2">
              <Card className="bg-white border-0 shadow-lg rounded-2xl overflow-hidden">
                <CardContent className="p-8">
                  {/* Donation Type Tabs */}
                  <Tabs defaultValue="one-time" className="mb-8">
                    <TabsList className="grid w-full grid-cols-2 bg-slate-50 rounded-full p-1">
                      <TabsTrigger 
                        value="one-time" 
                        onClick={() => setIsRecurring(false)}
                        className="rounded-full data-[state=active]:bg-centerra-purple data-[state=active]:text-white"
                        data-testid="donation-onetime-tab"
                      >
                        One-Time
                      </TabsTrigger>
                      <TabsTrigger 
                        value="monthly" 
                        onClick={() => setIsRecurring(true)}
                        className="rounded-full data-[state=active]:bg-centerra-purple data-[state=active]:text-white"
                        data-testid="donation-monthly-tab"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Monthly
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>

                  {/* Preset Amounts */}
                  <div className="mb-8">
                    <label className="text-sm font-semibold text-slate-700 mb-3 block">
                      Select Amount
                    </label>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                      {presetAmounts.map((preset) => (
                        <button
                          key={preset.id}
                          data-testid={`donation-${preset.id}`}
                          onClick={() => {
                            setSelectedAmount(preset.id);
                            setCustomAmount("");
                          }}
                          className={`py-4 px-4 rounded-xl font-semibold transition-colors ${
                            selectedAmount === preset.id
                              ? "bg-centerra-purple text-white"
                              : "bg-slate-50 text-centerra-purple hover:bg-centerra-purple/10"
                          }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Amount */}
                  <div className="mb-8">
                    <label className="text-sm font-semibold text-slate-700 mb-3 block">
                      Or Enter Custom Amount
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        data-testid="donation-custom-amount"
                        type="number"
                        placeholder="Enter amount"
                        value={customAmount}
                        onChange={(e) => {
                          setCustomAmount(e.target.value);
                          setSelectedAmount(null);
                        }}
                        min="5"
                        max="100000"
                        className="pl-12 h-14 rounded-xl border-slate-200 text-lg"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-2">Minimum $5 • Maximum $100,000</p>
                  </div>

                  {/* Donor Info (Optional) */}
                  <div className="mb-8 space-y-4">
                    <label className="text-sm font-semibold text-slate-700 block">
                      Your Information (Optional)
                    </label>
                    <Input
                      data-testid="donation-name"
                      placeholder="Your Name"
                      value={donorName}
                      onChange={(e) => setDonorName(e.target.value)}
                      className="h-12 rounded-xl border-slate-200"
                    />
                    <Input
                      data-testid="donation-email"
                      type="email"
                      placeholder="Email Address"
                      value={donorEmail}
                      onChange={(e) => setDonorEmail(e.target.value)}
                      className="h-12 rounded-xl border-slate-200"
                    />
                  </div>

                  {/* Payment Buttons */}
                  <div className="space-y-4">
                    <Button
                      data-testid="donate-stripe-button"
                      onClick={() => handleDonate("stripe")}
                      disabled={loading}
                      className="w-full bg-centerra-purple hover:bg-centerra-purple/90 text-white rounded-full py-6 text-lg font-semibold btn-centerra"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <CreditCard className="w-5 h-5 mr-2" />
                      )}
                      {loading ? "Processing..." : "Donate with Card"}
                    </Button>
                    
                    <Button
                      data-testid="donate-paypal-button"
                      onClick={() => handleDonate("paypal")}
                      disabled={loading}
                      variant="outline"
                      className="w-full border-2 border-[#0070BA] text-[#0070BA] hover:bg-[#0070BA]/5 rounded-full py-6 text-lg font-semibold"
                    >
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797H9.343a.796.796 0 0 0-.788.675l-.844 5.355z"/>
                      </svg>
                      Donate with PayPal
                    </Button>
                  </div>

                  <p className="text-xs text-slate-500 text-center mt-6">
                    Your donation is secure and encrypted. The Centerra Project is a registered 501(c)(4) organization.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="bg-white border-0 shadow-lg rounded-2xl overflow-hidden">
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-bold text-black mb-4">
                    Your Impact
                  </h3>
                  <ul className="space-y-3 text-sm text-slate-600">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-centerra-purple mt-0.5 flex-shrink-0" />
                      <span>Support data-driven policy research</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-centerra-purple mt-0.5 flex-shrink-0" />
                      <span>Train the next generation of leaders</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-centerra-purple mt-0.5 flex-shrink-0" />
                      <span>Advance pragmatic reforms in NM</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-centerra-purple mt-0.5 flex-shrink-0" />
                      <span>Build civic engagement programs</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-centerra-purple border-0 shadow-lg rounded-2xl overflow-hidden">
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-bold text-white mb-3">
                    Major Gift?
                  </h3>
                  <p className="text-white/80 text-sm mb-4">
                    For donations of $10,000+ or to discuss partnership opportunities, 
                    please contact us directly.
                  </p>
                  <a href="/#contact">
                    <Button 
                      variant="outline" 
                      className="w-full bg-transparent border-white/30 text-white hover:bg-white/10 rounded-full"
                    >
                      Contact Us
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Volunteer Page Component
const VolunteerPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    district: "",
    activity: "",
    other_activity: "",
    comments: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const volunteerActivities = [
    "Event Volunteering",
    "Community Outreach",
    "Phone Banking",
    "Door-to-Door Canvassing",
    "Social Media Advocacy",
    "Data Entry & Research",
    "Fundraising Support",
    "Other"
  ];

  const districts = [
    "Albuquerque",
    "Santa Fe",
    "Las Cruces",
    "Rio Rancho",
    "Roswell",
    "Farmington",
    "Los Alamos",
    "Clovis",
    "Hobbs",
    "Carlsbad",
    "Other / Statewide"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/volunteer`, formData);
      setSubmitted(true);
      toast.success("Thank you for volunteering! We'll be in touch soon.");
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-32 pb-20 px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-centerra-purple/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-centerra-purple" />
            </div>
            <h1 className="font-heading text-4xl font-bold text-black mb-4">
              Thank You for Taking Action!
            </h1>
            <p className="text-slate-600 text-lg mb-8">
              We've received your volunteer interest form and will reach out soon with opportunities 
              to get involved. Together, we can make a difference for New Mexico.
            </p>
            <div className="flex justify-center gap-4">
              <a href="/">
                <Button className="bg-centerra-purple hover:bg-centerra-purple/90 text-white rounded-full px-8 py-5">
                  Return Home
                </Button>
              </a>
              <a href="https://app.oath.vote/donate?p=centerra-c4" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-centerra-purple text-centerra-purple rounded-full px-8 py-5">
                  Make a Donation
                </Button>
              </a>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <span className="text-centerra-orange text-sm font-semibold tracking-widest uppercase">
              Take Action
            </span>
            <h1 
              data-testid="volunteer-headline"
              className="font-heading text-4xl md:text-5xl font-bold text-black mt-3 mb-4"
            >
              Get Involved
            </h1>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Join our community of engaged citizens working to create positive change in New Mexico. 
              Fill out the form below and we'll connect you with opportunities that match your interests.
            </p>
          </div>

          {/* Volunteer Form */}
          <Card className="bg-white border-0 shadow-lg rounded-2xl overflow-hidden">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name and Phone */}
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Full Name *
                    </label>
                    <Input
                      data-testid="volunteer-name"
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="h-12 rounded-lg border-slate-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Phone Number *
                    </label>
                    <Input
                      data-testid="volunteer-phone"
                      type="tel"
                      placeholder="(555) 555-5555"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="h-12 rounded-lg border-slate-200"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    data-testid="volunteer-email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="h-12 rounded-lg border-slate-200"
                  />
                </div>

                {/* District */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    District / Area *
                  </label>
                  <select
                    data-testid="volunteer-district"
                    value={formData.district}
                    onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                    required
                    className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-centerra-purple/20 focus:border-centerra-purple"
                  >
                    <option value="">Select your district/area</option>
                    {districts.map((district) => (
                      <option key={district} value={district}>{district}</option>
                    ))}
                  </select>
                </div>

                {/* Volunteer Activity */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    How would you like to help? *
                  </label>
                  <select
                    data-testid="volunteer-activity"
                    value={formData.activity}
                    onChange={(e) => setFormData({ ...formData, activity: e.target.value })}
                    required
                    className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-centerra-purple/20 focus:border-centerra-purple"
                  >
                    <option value="">Select an activity</option>
                    {volunteerActivities.map((activity) => (
                      <option key={activity} value={activity}>{activity}</option>
                    ))}
                  </select>
                </div>

                {/* Other Activity - Show only if "Other" is selected */}
                {formData.activity === "Other" && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Please specify
                    </label>
                    <Input
                      data-testid="volunteer-other"
                      placeholder="Describe how you'd like to help"
                      value={formData.other_activity}
                      onChange={(e) => setFormData({ ...formData, other_activity: e.target.value })}
                      className="h-12 rounded-lg border-slate-200"
                    />
                  </div>
                )}

                {/* Comments */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Additional Comments
                  </label>
                  <Textarea
                    data-testid="volunteer-comments"
                    placeholder="Tell us about your experience, availability, or any questions you have..."
                    value={formData.comments}
                    onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                    rows={4}
                    className="rounded-lg border-slate-200 resize-none"
                  />
                </div>

                <Button
                  data-testid="volunteer-submit"
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-centerra-purple hover:bg-centerra-purple/90 text-white rounded-full py-6 text-lg font-semibold btn-centerra"
                >
                  {submitting ? "Submitting..." : "Get Started"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Info Box */}
          <div className="mt-8 text-center text-slate-600">
            <p>Prefer to make a financial contribution instead? <a href="https://app.oath.vote/donate?p=centerra-c4" target="_blank" rel="noopener noreferrer" className="text-centerra-purple font-semibold hover:underline">Donate here</a></p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Main Home Page
const Home = () => {
  return (
    <div className="min-h-screen">
      <Navbar />
      <HeroSection />
      <MissionSection />
      <PillarsSection />
      <WhyNowSection />
      <VisionSection />
      <InvestmentSection />
      <ContactSection />
      <Footer />
    </div>
  );
};

// Import admin components
import { AdminLogin, AdminRegister, AdminDashboard } from "@/AdminDashboard";

function App() {
  return (
    <div>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/donate" element={<DonatePage />} />
          <Route path="/volunteer" element={<VolunteerPage />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/register/:token" element={<AdminRegister />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
